import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '@src/helpers/toaster'
import { useTranslation } from 'react-i18next'
import { IconPencil, IconTrash, IconBan, IconUserCheck } from '@tabler/icons-react'

import Tooltip from '@src/components/Tooltip'

import { useGetMyAccount } from '@queries/Account'
import { useUnsuspendTeamUser } from '@queries/Teams'
import { useHasPermission } from '@helpers/hooks/useHasPermission'

import routes from '@src/Routes/routes'
import { Perm } from '@type/permissions'
import type { OrgStructureRecord } from '@pages/Team/Members/types'
import { requestErrorsHandler } from '@src/helpers/utils'

import styles from './index.module.css'

export interface ActionsProps {
  record: OrgStructureRecord
  onDeleteRecord: (record: OrgStructureRecord) => void
  onSuspendRecord: (record: OrgStructureRecord) => void
}

// Member row actions: Edit / Suspend / Delete. All three gate on
// team.manage_member_profiles — that's the codename the backend
// uses for the DELETE / PATCH / suspend endpoints. A view-only role
// with team.view sees the row but no controls.
const Actions: React.FC<ActionsProps> = ({ record, onDeleteRecord, onSuspendRecord }) => {
  const { data: account } = useGetMyAccount()
  const { mutateAsync: unsuspendUser } = useUnsuspendTeamUser(record.id)
  const canManageProfiles = useHasPermission(Perm.TEAM_MANAGE_MEMBER_PROFILES)
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  const handleEditClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e?.stopPropagation()
      navigate(routes.private.organizationalStructureById.replace(':id', record.id.toString()))
    },
    [record],
  )

  const handleDeleteClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e?.stopPropagation()
      onDeleteRecord(record)
    },
    [record],
  )

  const handleSuspendClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (e) => {
      e?.stopPropagation()
      if (record.status === 'suspended') {
        try {
          await unsuspendUser({})
          toast.success('User unsuspended successfully')
        } catch (err) {
          requestErrorsHandler(err)
        }
      } else {
        onSuspendRecord(record)
      }
    },
    [record, unsuspendUser, onSuspendRecord],
  )

  const isCurrentUser = account?.id === record.auth_user_id

  if (!canManageProfiles) return null

  return (
    <>
      <div className="grid grid-cols-3 gap-1">
        <button
          data-tooltip-id="actions_tip"
          data-tooltip-content={t('usersTips.edit')}
          onClick={handleEditClick}
          className={styles.actionsButton}
          data-qa="edit-button"
        >
          <IconPencil className="h-4 w-4" />
        </button>

        <div className="h-4">
          {!isCurrentUser && (
            <button
              data-tooltip-content={record.status === 'suspended' ? 'Unsuspend' : 'Suspend'}
              data-tooltip-id="actions_tip"
              onClick={handleSuspendClick}
              className={styles.actionsButton}
              data-qa="suspend-button"
            >
              {record.status === 'suspended' ? <IconUserCheck className="h-4 w-4" /> : <IconBan className="h-4 w-4" />}
            </button>
          )}
        </div>

        <div className="h-4">
          {!isCurrentUser && (
            <button
              data-tooltip-content={t('usersTips.delete')}
              data-tooltip-id="actions_tip"
              onClick={handleDeleteClick}
              className={styles.actionsButton}
              data-qa="delete-button"
            >
              <IconTrash className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <Tooltip place="left" id="actions_tip" />
    </>
  )
}

export default Actions
